import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from ".";
import { resolveAvatarImage } from "../../services/functions";
import { chatFetch } from "../../services/chatRequest";
import { ENTITLEMENT_REFUSED, isEntitlementRequired } from "../../services/entitlementPrompt";

// Action to get or create a Stream Chat user
export const getOrCreateStreamChatUser = createAsyncThunk(
  "streamChat/getOrCreateUser",
  async (userConfig, { rejectWithValue }) => {
    try {
      const response = await chatFetch(`${baseUrl}/api/v1/chat/users/settings`, {
        method: "GET",
        headers: headersDefault(),
      });

      if (!response.ok) {
        // If it's 404, the user doesn't exist in Stream Chat
        if (response.status === 404) {
          throw new Error("STREAM_USER_NOT_FOUND");
        }

        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error verifying user:", errorData);
        throw new Error(
          errorData.message || "Error verificando usuario de Stream Chat",
        );
      }

      const data = await response.json();

      return {
        streamUserId: data.user.id, // The ID of the Stream Chat user
        displayName: data.user.display_name,
        fullName: data.user.full_name,
        avatarUrl: data.user.avatar_url,
        apiKey: data.api_key,
        token: data.token,
        exists: true,
      };
    } catch (error) {
      console.error("Error in getOrCreateStreamChatUser:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to create Stream Chat user
export const createStreamChatUser = createAsyncThunk(
  "streamChat/createUser",
  async (displayName, { rejectWithValue }) => {
    try {
      // Validate and sanitize display_name
      // Trim whitespace and ensure it has at least 4 characters (backend requirement)
      let validDisplayName = displayName ? displayName.trim() : "";
      if (!validDisplayName || validDisplayName.length < 4) {
        validDisplayName = "Invitato";
      }

      const response = await chatFetch(`${baseUrl}/api/v1/chat/users`, {
        method: "POST",
        headers: headersDefault(),
        body: JSON.stringify({ display_name: validDisplayName }),
      });


      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error creating user:", errorData);
        throw new Error(
          errorData.message || "Error creando usuario de Stream Chat",
        );
      }

      const data = await response.json();

      return {
        streamUserId: data.id, // "user-new-2"
        displayName: data.name,
        image: data.image,
        systemUserId: data.system_user_id,
        chatProviderData: data.chat_provider_data,
      };
    } catch (error) {
      console.error("Error in createStreamChatUser:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to create Stream Chat token
export const createStreamChatToken = createAsyncThunk(
  "streamChat/createToken",
  async (userId, { rejectWithValue }) => {
    try {
      const userIdString = userId.toString();

      const response = await chatFetch(
        `${baseUrl}/api/v1/chat/users/token/create`,
        {
          method: "POST",
          headers: headersDefault(),
          body: JSON.stringify({
            user_id: userIdString, // We need to send the user_id for the backend to generate the correct token
          }),
        },
      );


      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error response from backend:", errorData);

        // If it's 404, it means that the Stream Chat user doesn't exist
        if (response.status === 404) {
          throw new Error("STREAM_USER_NOT_FOUND");
        }

        throw new Error(
          errorData.message || "Error creating Stream Chat token",
        );
      }

      const data = await response.json();

      if (!data.token) {
        throw new Error("Token not found in the response from the server");
      }

      return {
        token: data.token,
        userId: userIdString,
        createdAt: new Date().toISOString(),
      };
    } catch (error) {
      console.error("Error in createStreamChatToken:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to get user information for Stream Chat
export const getStreamChatUserInfo = createAsyncThunk(
  "streamChat/getUserInfo",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const userState = state.user;
      const user = userState?.user; // The real user is in state.user.user

      if (!user || !user.id) {
        console.error("Error: User not found or without ID", {
          userState,
          user,
        });
        throw new Error("User not authenticated or without ID");
      }

      // Get nickname from master_club_subscriber or subscriber
      const rawNickname =
        userState?.master_club_subscriber?.nickname ||
        user?.subscriber?.nickname ||
        null;
      const nickname =
        rawNickname && rawNickname.trim() ? rawNickname.trim() : null;

      // Get full name (name + surname)
      const rawFullName = `${user.name || ""} ${user.surname || ""}`.trim();
      const fullName =
        rawFullName && rawFullName.length >= 4
          ? rawFullName
          : user.nickname && user.nickname.trim()
          ? user.nickname.trim()
          : "User";

      // Determine display_name: use nickname if available and valid, otherwise use full_name
      // Ensure display_name has at least 4 characters (backend requirement)
      let displayName = nickname && nickname.length >= 4 ? nickname : fullName;
      if (!displayName || displayName.trim().length < 4) {
        displayName = "User";
      }

      // Configure user data for Stream Chat
      const userConfig = {
        id: user.id.toString(),
        display_name: displayName,
        full_name: fullName,
        nickname: nickname, // Store nickname separately for reference
        avatar_url: user.avatar_url || user.profile_picture_url || user.avatar,
        // Datos adicionales para Stream Chat
        role: user.role || "user",
        custom: {
          subscription_type:
            userState?.master_club_subscriber?.subscription?.subscription_type,
          is_premium:
            userState?.master_club_subscriber?.subscription?.subscription_type
              ?.name === "premium",
          email: user.email,
          // System user id so peers can open this user's public profile (/u/:id)
          // by tapping their nickname/avatar in group chats & DMs.
          system_user_id: user.id?.toString() ?? null,
          // Public badge ({ name, color }) shown next to the nickname in messages.
          // Distinct from account-status (subscription_type); null for most users.
          badge: userState?.master_club_subscriber?.badge ?? null,
        },
      };

      return userConfig;
    } catch (error) {
      console.error("Error in getStreamChatUserInfo:", error);
      return rejectWithValue(error.message);
    }
  },
);

let initializationInFlight = null;
let initializationCache = null;

const loadPoliciesInBackground = (dispatch) => {
  dispatch(getUserPolicies()).then((result) => {
    if (result.type.endsWith("/rejected")) {
      console.warn("Failed to load user policies:", result.payload);
    }
  });
};

const performStreamChatInitialization = async (displayName, dispatch) => {
  const userInfoResult = await dispatch(getStreamChatUserInfo());

  if (userInfoResult.type.endsWith("/rejected")) {
    throw new Error(userInfoResult.payload);
  }

  const systemUserConfig = userInfoResult.payload;
  const streamUserResult = await dispatch(
    getOrCreateStreamChatUser(systemUserConfig),
  );

  if (!streamUserResult.type.endsWith("/rejected")) {
    const existingStreamUser = streamUserResult.payload;
    loadPoliciesInBackground(dispatch);

    return {
      userConfig: {
        id: existingStreamUser.streamUserId,
        name: existingStreamUser.displayName,
        image: existingStreamUser.avatarUrl,
        role: systemUserConfig.role,
        custom: systemUserConfig.custom,
      },
      token: existingStreamUser.token,
      apiKey: existingStreamUser.apiKey,
      isInitialized: true,
      userCreated: false,
    };
  }

  const streamUserError = streamUserResult.payload;
  if (streamUserError !== "STREAM_USER_NOT_FOUND") {
    throw new Error(streamUserError);
  }

  const getValidDisplayName = (name) => {
    if (!name) return null;
    const trimmed = name.trim();
    return trimmed && trimmed.length >= 4 ? trimmed : null;
  };
  const autoDisplayName =
    (displayName?.trim()?.length >= 4 && displayName.trim()) ||
    getValidDisplayName(systemUserConfig.display_name) ||
    getValidDisplayName(systemUserConfig.full_name) ||
    getValidDisplayName(systemUserConfig.nickname) ||
    "User";

  const createUserResult = await dispatch(
    createStreamChatUser(autoDisplayName),
  );
  if (createUserResult.type.endsWith("/rejected")) {
    throw new Error(createUserResult.payload);
  }

  const newStreamUser = createUserResult.payload;
  const tokenResult = await dispatch(
    createStreamChatToken(newStreamUser.streamUserId),
  );
  if (tokenResult.type.endsWith("/rejected")) {
    throw new Error(tokenResult.payload);
  }

  loadPoliciesInBackground(dispatch);
  return {
    userConfig: {
      id: newStreamUser.streamUserId,
      name: newStreamUser.displayName,
      image: newStreamUser.image,
      role: systemUserConfig.role,
      custom: systemUserConfig.custom,
    },
    token: tokenResult.payload.token,
    isInitialized: true,
    userCreated: true,
  };
};

// One shared initialization per signed-in system user. The app-wide bootstrap
// and Chat page can mount together; both await this promise instead of issuing
// duplicate settings/token requests.
export const initializeStreamChat = createAsyncThunk(
  "streamChat/initialize",
  async (displayName = null, { dispatch, getState, rejectWithValue }) => {
    const systemUserId = String(getState()?.user?.user?.id || "");

    try {
      if (
        initializationCache?.systemUserId === systemUserId &&
        initializationCache.result
      ) {
        return initializationCache.result;
      }

      if (
        !initializationInFlight ||
        initializationInFlight.systemUserId !== systemUserId
      ) {
        const promise = performStreamChatInitialization(displayName, dispatch);
        initializationInFlight = { systemUserId, promise };
      }

      const currentPromise = initializationInFlight.promise;
      const result = await currentPromise;
      initializationCache = { systemUserId, result };
      return result;
    } catch (error) {
      console.error("Error in initializeStreamChat:", error);
      return rejectWithValue(error.message);
    } finally {
      if (
        initializationInFlight?.systemUserId === systemUserId &&
        initializationInFlight.promise
      ) {
        initializationInFlight = null;
      }
    }
  },
);

// Action to fetch complete channel data from backend
export const fetchChannelData = createAsyncThunk(
  "streamChat/fetchChannelData",
  async (channelId, { rejectWithValue }) => {
    try {
      const response = await chatFetch(
        `${baseUrl}/api/v1/chat/groups/${channelId}/fetch`,
        {
          method: "GET",
          headers: headersDefault(),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error fetching channel data");
      }

      const responseData = await response.json();

      // Extract data from chat_provider_data (may be null for 1:1 chats)
      const data = responseData.chat_provider_data;

      // Transform backend data to a more usable format
      const result = {
        id: responseData.id,
        groupId: responseData.provider_group_id,
        ownerId: responseData.owner_chat_user_id,
        name: responseData.name,
        description: responseData.description || null,
        isPublic: responseData.is_public,
        isModerated: responseData.is_moderated,
        groupType: responseData.group_type || null,
        isMember: responseData.is_member ?? false,
        canAccess:
          responseData.can_access === undefined
            ? undefined
            : Boolean(responseData.can_access),
        requirePhoneVerification: Boolean(
          responseData.require_phone_verification,
        ),
        imageUrl: responseData.image_url || responseData.group_image || null,
      };

      // Only include chat_provider_data fields when available
      if (data) {
        result.channel = {
          id: data.channel?.id,
          type: data.channel?.type,
          createdAt: data.channel?.created_at,
          createdBy: data.channel?.created_by,
          memberCount: data.channel?.member_count,
          config: data.channel?.config,
          capabilities: data.channel?.own_capabilities,
        };
        result.members = (data.members || []).map((member) => ({
          id: member.user.id,
          name: member.user.name,
          role: member.role,
          premium: member.user.custom?.is_premium || false,
          subscription: member.user.custom?.subscription_type,
        }));
        result.messages = data.messages || [];
        result.reads = (data.read || []).map((read) => ({
          userId: read.user.id,
          lastRead: read.last_read,
          unread: read.unread_messages,
        }));
      }

      return result;
    } catch (error) {
      console.error("Error in fetchChannelData:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to get chat groups list from backend
export const getChatGroupsList = createAsyncThunk(
  "streamChat/getGroupsList",
  async (requestBody = {}, { rejectWithValue }) => {
    try {
      // Check if this is a private (1:1) endpoint request
      const isPrivateListEndpoint =
        requestBody.criteria &&
        Array.isArray(requestBody.criteria.group_types_included) &&
        requestBody.criteria.group_types_included.length === 1 &&
        requestBody.criteria.group_types_included[0] === "private";

      // Build query parameters from requestBody
      const queryParams = new URLSearchParams();

      if (isPrivateListEndpoint) {
        // For /list/private endpoint: only send name_contains (search) inside criteria array format
        const searchTerm = requestBody.criteria?.name_contains || "";
        if (searchTerm) {
          queryParams.append("criteria[name_contains]", searchTerm);
        }
      } else {
        // For other endpoints, include all criteria fields
        if (requestBody.criteria) {
          const { criteria } = requestBody;

          // Add name_contains if provided
          if (criteria.name_contains) {
            queryParams.append(
              "criteria[name_contains]",
              criteria.name_contains,
            );
          }

          // Add group_types_included as array
          if (
            criteria.group_types_included &&
            criteria.group_types_included.length > 0
          ) {
            criteria.group_types_included.forEach((type) => {
              queryParams.append("criteria[group_types_included][]", type);
            });
          }

          // Add is_member (convert boolean to 1/0)
          if (criteria.is_member !== undefined) {
            queryParams.append(
              "criteria[is_member]",
              criteria.is_member ? "1" : "0",
            );
          }

          // Add other criteria fields if needed (convert boolean to 1/0)
          if (criteria.is_public !== undefined) {
            queryParams.append(
              "criteria[is_public]",
              criteria.is_public ? "1" : "0",
            );
          }

          if (criteria.is_moderated !== undefined) {
            queryParams.append(
              "criteria[is_moderated]",
              criteria.is_moderated ? "1" : "0",
            );
          }
        }
      }

      // Add pagination parameters (always included)
      if (requestBody.page !== undefined) {
        queryParams.append("page", requestBody.page.toString());
      }

      if (requestBody.limit !== undefined) {
        queryParams.append("limit", requestBody.limit.toString());
      }

      // Build URL with query parameters
      const queryString = queryParams.toString();

      // For 1:1 (private) chats, use the dedicated /list/private endpoint
      const basePath = isPrivateListEndpoint
        ? `${baseUrl}/api/v1/chat/groups/list/private`
        : `${baseUrl}/api/v1/chat/groups/list`;

      const url = queryString ? `${basePath}?${queryString}` : basePath;

      // Use GET with query parameters (like axios.get with params)
      const response = await chatFetch(url, {
        method: "GET",
        headers: headersDefault(),
      });

      if (!response.ok) {
        // If it's 404, treat it as "no results found" rather than an error
        if (response.status === 404) {
          return {
            groups: [],
            total: 0,
          };
        }

        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error fetching groups list:", errorData);
        throw new Error(errorData.message || "Error fetching chat groups list");
      }

      const data = await response.json();

      // Transform backend data to a more usable format
      const groups = data.data.map((group) => {
        // New 1:1 endpoint format with peer/myself info
        if (group.peer && group.myself) {
          const peer = group.peer;
          const owner = group.owner || group.myself.id;
          // For private chats, unread for current user is in `myself.unread.count`.
          // Fallback to top-level unread only for backward compatibility.
          const unreadCount =
            Number(group.myself?.unread?.count) ||
            Number(group.unread?.count) ||
            0;

          console.log(
            `[ChatUnread][private] id=${group.id} name="${peer.name || "Utente"}" unreadCount=${unreadCount}`,
          );

          return {
            // In the 1:1 endpoint, "id" is already the provider group ID (e.g. "stage-group-23")
            id: group.id,
            providerGroupId: group.id,
            ownerChatUserId: owner,
            // Always use peer name and avatar for display
            name: peer.name || "Utente",
            description: null,
            imageUrl: resolveAvatarImage(peer.avatar),
            // Recency ordering, same as the group list.
            lastMessageAt: group.last_message_at || null,
            // 1:1 chats are always private between two users
            isPublic: false,
            isModerated: false,
            isMember: true,
            canAccess: true,
            numberOfMembers: 2,
            // Preserve "private" semantics for existing UI logic
            chatGroupTypeId: 4,
            // Mark scope as user so ChatView can treat it as 1:1 chat
            scope: "user",
            groupType: {
              id: 4,
              name: "private",
              description: "Gruppi privati",
              is_enabled: true,
              is_verified: false,
              scope: "user",
              image_path: null,
              image_url: null,
              allowed_user_types: null,
            },
            chatProviderData: {
              cid: group.cid,
              myself: group.myself,
              peer: group.peer,
            },
            unreadCount,
          };
        }

        // Legacy/group endpoints format
        const unreadCount = Number(group.unread?.count) || 0;
        console.log(
          `[ChatUnread][group] id=${group.provider_group_id || group.id} name="${group.name || ""}" unreadCount=${unreadCount}`,
        );

        return {
          id: group.id,
          providerGroupId: group.provider_group_id, // "group-new-1"
          ownerChatUserId: group.owner_chat_user_id, // "user-new-1"
          name: group.name,
          description: group.description,
          imageUrl: group.image_url,
          isPublic: group.is_public,
          isModerated: group.is_moderated,
          isMember: group.is_member, // Add is_member flag
          canAccess:
            group.can_access === undefined ? undefined : Boolean(group.can_access),
          numberOfMembers: group.number_of_members || 0, // Add number_of_members
          chatGroupTypeId: group.chat_group_type_id, // ID del tipo de grupo
          groupType: group.group_type || null, // Include group_type with scope
          chatProviderData: group.chat_provider_data,
          // Drives the chat list's recency ordering (see chatRecency in
          // pages/Chat) so a chat that receives a message while the list is open
          // floats to the top without a refetch.
          lastMessageAt: group.last_message_at || null,
          unreadCount,
        };
      });

      return {
        groups,
        total: data.total || data.data.length,
      };
    } catch (error) {
      console.error("Error in getChatGroupsList:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to create a new chat group/channel via backend
export const createChatGroup = createAsyncThunk(
  "streamChat/createGroup",
  async (groupData, { rejectWithValue }) => {
    try {
      console.log("Debug - Creating chat group with data:", groupData);

      const requestBody = {
        name: groupData.name,
        description: groupData.description || null,
        group_type_id: groupData.groupTypeId,
      };

      // Add image_data if provided
      if (groupData.imageData) {
        requestBody.image_data = groupData.imageData;
      }

      const response = await chatFetch(`${baseUrl}/api/v1/chat/groups`, {
        method: "POST",
        headers: headersDefault(),
        body: JSON.stringify(requestBody),
      });

      console.log(
        "Debug - Response status for creating group:",
        response.status,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error creating group:", errorData);
        throw new Error(errorData.message || "Error creating chat group");
      }

      const data = await response.json();

      console.log("Debug - Group created successfully:", data);

      return {
        id: data.id,
        providerGroupId: data.provider_group_id, // "group-new-1"
        ownerChatUserId: data.owner_chat_user_id, // "user-new-1"
        name: data.name,
        description: data.description,
        imageUrl: data.image_url,
        isPublic: data.is_public,
        isModerated: data.is_moderated,
        chatGroupTypeId: data.chat_group_type_id, // ID del tipo de grupo
        chatProviderData: data.chat_provider_data,
      };
    } catch (error) {
      console.error("Error in createChatGroup:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to get chat users list from backend
export const getChatUsersList = createAsyncThunk(
  "streamChat/getUsersList",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Debug - Fetching chat users list from backend");

      const response = await chatFetch(`${baseUrl}/api/v1/chat/users/list`, {
        method: "GET",
        headers: headersDefault(),
      });

      console.log("Debug - Response status for users list:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error fetching users list:", errorData);
        throw new Error(errorData.message || "Error fetching chat users list");
      }

      const data = await response.json();

      console.log("Debug - Users list fetched successfully:", data);

      // Transform backend data to a more usable format
      const users = data.data.map((user) => ({
        id: user.id,
        name: user.name,
        image: user.image,
        systemUserId: user.system_user_id,
        chatProviderData: user.chat_provider_data,
      }));

      return {
        users,
        total: data.data.length,
      };
    } catch (error) {
      console.error("Error in getChatUsersList:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to get chat operators from backend
export const getChatOperators = createAsyncThunk(
  "streamChat/getOperators",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Debug - Fetching chat operators from backend");

      const response = await chatFetch(`${baseUrl}/api/v1/chat/users/operators`, {
        method: "GET",
        headers: headersDefault(),
      });

      console.log("Debug - Response status for operators:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error fetching operators:", errorData);
        throw new Error(errorData.message || "Error fetching chat operators");
      }

      const data = await response.json();

      console.log("Debug - Operators fetched successfully:", data);

      // Transform backend data to a more usable format
      const operators = data.data.map((operator) => ({
        id: operator.id,
        name: operator.name,
        image: operator.image,
        systemUserId: operator.system_user_id,
        chatUserTypeId: operator.chat_user_type_id,
        chatProviderData: operator.chat_provider_data,
      }));

      return {
        operators,
        total: data.total || data.data.length,
      };
    } catch (error) {
      console.error("Error in getChatOperators:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to add member to chat group via backend
export const addGroupMember = createAsyncThunk(
  "streamChat/addGroupMember",
  async (membershipData, { rejectWithValue }) => {
    try {
      console.log("Debug - Adding member to group with data:", membershipData);

      const response = await chatFetch(`${baseUrl}/api/v1/chat/group-memberships`, {
        method: "POST",
        headers: headersDefault(),
        body: JSON.stringify({
          user: membershipData.user,
          group: membershipData.group,
          is_admin: membershipData.isAdmin || false,
          is_moderator: membershipData.isModerator || false,
          is_muted: membershipData.isMuted || false,
        }),
      });

      console.log(
        "Debug - Response status for adding member:",
        response.status,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error adding member:", errorData);
        // Premium Lite chat limit: the app-wide popup already explains it.
        if (isEntitlementRequired(response.status, errorData)) {
          return rejectWithValue(ENTITLEMENT_REFUSED);
        }
        throw new Error(errorData.message || "Error adding member to group");
      }

      const data = await response.json();

      console.log("Debug - Member added successfully:", data);

      return {
        id: data.id,
        group: {
          id: data.group.id,
          providerGroupId: data.group.provider_group_id,
          ownerChatUserId: data.group.owner_chat_user_id,
          name: data.group.name,
          description: data.group.description,
          imageUrl: data.group.image_url,
          isPublic: data.group.is_public,
          isModerated: data.group.is_moderated,
          chatProviderData: data.group.chat_provider_data,
        },
        user: {
          id: data.user.id,
          name: data.user.name,
          image: data.user.image,
          systemUserId: data.user.system_user_id,
          chatProviderData: data.user.chat_provider_data,
        },
        isAdmin: data.is_admin,
        isModerator: data.is_moderator,
        isMuted: data.is_muted,
        members: data.members,
      };
    } catch (error) {
      console.error("Error in addGroupMember:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to delete a chat group via backend
export const deleteChatGroup = createAsyncThunk(
  "streamChat/deleteGroup",
  async (providerGroupId, { rejectWithValue }) => {
    try {
      console.log("Debug - Deleting chat group:", providerGroupId);

      const response = await chatFetch(
        `${baseUrl}/api/v1/chat/groups/${providerGroupId}`,
        {
          method: "DELETE",
          headers: headersDefault(),
        },
      );

      console.log(
        "Debug - Response status for deleting group:",
        response.status,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error deleting group:", errorData);
        throw new Error(errorData.message || "Error deleting chat group");
      }

      // Try to parse JSON if response has content, otherwise treat as empty response
      let data = null;
      try {
        const text = await response.text();
        if (text.trim()) {
          data = JSON.parse(text);
        }
      } catch (parseError) {
        // Response is empty or not JSON, which is fine for DELETE requests
        console.log(
          "Debug - Response is empty or not JSON (this is normal for DELETE)",
        );
      }

      console.log(
        "Debug - Group deleted successfully:",
        data || "No response body",
      );

      return {
        providerGroupId: providerGroupId,
        deleted: true,
      };
    } catch (error) {
      console.error("Error in deleteChatGroup:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to update a chat group
export const updateChatGroup = createAsyncThunk(
  "streamChat/updateGroup",
  async ({ providerGroupId, updateData }, { rejectWithValue }) => {
    try {
      console.log("Debug - Updating chat group:", providerGroupId, updateData);

      const response = await chatFetch(
        `${baseUrl}/api/v1/chat/groups/${providerGroupId}`,
        {
          method: "PUT",
          headers: headersDefault(),
          body: JSON.stringify(updateData),
        },
      );

      console.log(
        "Debug - Response status for updating group:",
        response.status,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error updating group:", errorData);
        throw new Error(errorData.message || "Error updating chat group");
      }

      const data = await response.json();

      console.log("Debug - Group updated successfully:", data);

      return {
        id: data.id,
        providerGroupId: data.provider_group_id,
        ownerChatUserId: data.owner_chat_user_id,
        name: data.name,
        description: data.description,
        imageUrl: data.image_url,
        isPublic: data.is_public,
        isModerated: data.is_moderated,
        chatGroupTypeId: data.chat_group_type_id,
        chatProviderData: data.chat_provider_data,
      };
    } catch (error) {
      console.error("Error in updateChatGroup:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to get user settings for editing
export const getUserSettings = createAsyncThunk(
  "streamChat/getUserSettings",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Debug - Fetching user settings for editing");

      const response = await chatFetch(`${baseUrl}/api/v1/chat/users/settings`, {
        method: "GET",
        headers: headersDefault(),
      });

      console.log(
        "Debug - Response status for user settings:",
        response.status,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error fetching user settings:", errorData);
        throw new Error(errorData.message || "Error fetching user settings");
      }

      const data = await response.json();

      console.log("Debug - User settings fetched successfully:", data);

      return {
        user: {
          id: data.user.id,
          displayName: data.user.display_name,
          fullName: data.user.full_name,
          avatarUrl: data.user.image, // Changed from data.user.avatar_url to data.user.image
        },
        apiKey: data.api_key,
        token: data.token,
      };
    } catch (error) {
      console.error("Error in getUserSettings:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to update user settings
export const updateUserSettings = createAsyncThunk(
  "streamChat/updateUserSettings",
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      console.log("Debug - Updating user settings:", { userId, userData });

      const response = await chatFetch(`${baseUrl}/api/v1/chat/users/${userId}`, {
        method: "PUT",
        headers: headersDefault(),
        body: JSON.stringify(userData),
      });

      console.log(
        "Debug - Response status for updating user:",
        response.status,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error updating user:", errorData);
        throw new Error(errorData.message || "Error updating user settings");
      }

      const data = await response.json();

      console.log("Debug - User updated successfully:", data);

      return {
        user: {
          id: data.id,
          displayName: data.display_name,
          fullName: data.full_name,
          avatarUrl: data.image, // Changed from data.avatar_url to data.image
        },
      };
    } catch (error) {
      console.error("Error in updateUserSettings:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to get channel members list
export const getChannelMembersList = createAsyncThunk(
  "streamChat/getChannelMembersList",
  async ({ group }, { rejectWithValue }) => {
    try {
      console.log("Debug - Fetching channel members list for group:", group);

      const response = await chatFetch(
        `${baseUrl}/api/v1/chat/group-memberships/list?group=${group}`,
        {
          method: "GET",
          headers: headersDefault(),
        },
      );

      console.log("Debug - Response status for members list:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error fetching members list:", errorData);
        throw new Error(
          errorData.message || "Error fetching channel members list",
        );
      }

      const data = await response.json();

      console.log("Debug - Members list fetched successfully:", data);

      // Transform backend data to a more usable format
      // The backend returns individual memberships, we need to extract unique members
      const membersMap = new Map();

      data.data.forEach((membership) => {
        // Each membership represents a user's membership in the group
        // The membership.id is what we need for deletion
        const memberId = membership.user.id;
        if (!membersMap.has(memberId)) {
          membersMap.set(memberId, {
            id: memberId,
            membershipId: membership.id, // This is the ID we need for DELETE (e.g., 14)
            userId: membership.user.id,
            name: membership.user.name,
            // Live nickname-aware name (nickname ?: full name) — like the feed.
            subscriberFullName:
              membership.user.system_user?.subscriber_full_name ?? null,
            // Real "Nome Cognome" and the nickname on its own. Stream's
            // user.name collapses to the nickname whenever one is set, so both
            // are needed to label a message "Nome Cognome (nickname)".
            fullName: membership.user.system_user?.name ?? null,
            nickname: membership.user.system_user?.nickname ?? null,
            image: membership.user.image,
            systemUserId:
              membership.user.system_user_id ??
              membership.user.systemUserId ??
              membership.user.system_user?.id ??
              null,
            systemProfileId:
              membership.user.system_user?.id ??
              membership.user.system_user_id ??
              membership.user.systemUserId ??
              null,
            hasBadge:
              membership.user.has_badge ??
              membership.user.system_user?.has_badge ??
              false,
            badge:
              membership.user.badge ??
              membership.user.system_user?.badge ??
              null,
            role: membership.members
              ? membership.members.find((m) => m.user_id === memberId)?.role
              : "member",
            status: membership.members
              ? membership.members.find((m) => m.user_id === memberId)?.status
              : "member",
            isAdmin: membership.is_admin,
            isModerator: membership.is_moderator,
            isMuted: membership.is_muted,
            createdAt: membership.members
              ? membership.members.find((m) => m.user_id === memberId)
                  ?.created_at
              : null,
          });
        }
      });

      const members = Array.from(membersMap.values());

      return {
        members,
        total: members.length,
        // Which channel this roster belongs to — the slice holds one at a time,
        // so callers need it to tell a warm cache from another channel's data.
        group,
        // Backend-authoritative: only badge-holders may remove members. Drives
        // whether the remove "X" is shown in the members list.
        canRemoveMembers: data.can_remove_members === true,
      };
    } catch (error) {
      console.error("Error in getChannelMembersList:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to remove channel member
export const removeChannelMember = createAsyncThunk(
  "streamChat/removeChannelMember",
  async ({ userId, groupId }, { rejectWithValue }) => {
    try {
      console.log("Debug - Removing channel member:", { userId, groupId });

      const response = await chatFetch(
        `${baseUrl}/api/v1/chat/group-memberships/${groupId}`,
        {
          method: "DELETE",
          headers: {
            ...headersDefault(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user: userId,
            group: groupId,
          }),
        },
      );

      console.log(
        "Debug - Response status for removing member:",
        response.status,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error removing member:", errorData);
        // Backend errors come back as { error: { message } } (e.g. the 403 when a
        // non-badge user is blocked); fall back to the flat shape just in case.
        throw new Error(
          errorData.error?.message ||
            errorData.message ||
            "Error removing channel member",
        );
      }

      // Check if response has content before trying to parse JSON
      const responseText = await response.text();
      let data = null;

      if (responseText.trim()) {
        try {
          data = JSON.parse(responseText);
          console.log("Debug - Member removed successfully:", data);
        } catch (parseError) {
          console.log("Debug - Response is not JSON, treating as success");
          data = { success: true };
        }
      } else {
        console.log("Debug - Empty response, treating as success");
        data = { success: true };
      }

      return {
        userId: userId,
        groupId: groupId,
        removed: true,
      };
    } catch (error) {
      console.error("Error in removeChannelMember:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to get chat group types for support
export const getChatGroupTypes = createAsyncThunk(
  "streamChat/getGroupTypes",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Debug - Fetching chat group types with support scope");

      const requestBody = {
        scope: "support",
      };

      console.log("Debug - Request body:", requestBody);

      const response = await chatFetch(`${baseUrl}/api/v1/chat/groups/types`, {
        method: "POST", // Change to POST to send body
        headers: headersDefault(),
        body: JSON.stringify(requestBody),
      });

      console.log("Debug - Response status for group types:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error fetching group types:", errorData);
        throw new Error(errorData.message || "Error fetching chat group types");
      }

      const data = await response.json();

      console.log("Debug - Group types fetched successfully:", data);

      // Transform backend data to a more usable format
      const types = data.data
        ? data.data.map((type) => ({
            id: type.id,
            name: type.name,
            description: type.description,
            isEnabled: type.is_enabled,
            image: type.image_url || null, // Add image support
          }))
        : [];

      return {
        groupTypes: types, // Change to groupTypes to match expected structure
        total: types.length,
      };
    } catch (error) {
      console.error("Error in getChatGroupTypes:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to get user policies/permissions
export const getUserPolicies = createAsyncThunk(
  "streamChat/getUserPolicies",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Debug - Fetching user policies");

      const response = await chatFetch(`${baseUrl}/api/v1/chat/users/policies`, {
        method: "GET",
        headers: headersDefault(),
      });

      console.log(
        "Debug - Response status for user policies:",
        response.status,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error fetching user policies:", errorData);
        throw new Error(errorData.message || "Error fetching user policies");
      }

      const data = await response.json();

      console.log("Debug - User policies fetched successfully:", data);

      // Transform backend data to a more usable format
      // Only include permissions with "allow" access
      const allowedPermissions = data.data
        .filter((policy) => policy.access === "allow")
        .map((policy) => ({
          id: policy.permission.id,
          name: policy.permission.name,
          description: policy.permission.description,
          source: {
            id: policy.source.id,
            type: policy.source.type,
          },
          access: policy.access,
          priority: policy.priority,
        }));

      // Create a simple permissions object for easy checking
      const permissions = {};
      allowedPermissions.forEach((permission) => {
        permissions[permission.name] = true;
      });

      return {
        policies: allowedPermissions,
        permissions,
        total: allowedPermissions.length,
      };
    } catch (error) {
      console.error("Error in getUserPolicies:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to clear the Stream Chat state
export const clearStreamChatState = createAsyncThunk(
  "streamChat/clearState",
  async () => {
    initializationInFlight = null;
    initializationCache = null;
    return {
      token: null,
      userConfig: null,
      isInitialized: false,
      error: null,
    };
  },
);

// Action to get support types from backend
export const getSupportTypes = createAsyncThunk(
  "streamChat/getSupportTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatFetch(`${baseUrl}/api/v1/chat/support`, {
        method: "GET",
        headers: headersDefault(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error getting support types:", errorData);
        throw new Error(errorData.message || "Error getting support types");
      }

      const data = await response.json();

      // Extract support types from response structure
      const supportTypesArray = data?.data || [];

      // Transform backend data to frontend format
      const transformedSupportTypes = supportTypesArray.map((type) => ({
        id: type.id,
        name: type.name,
        description: type.description,
        isEnabled: type.is_enabled,
        isVerified: type.is_verified,
        scope: type.scope,
        // Keep original backend URL so we can derive the support type key (last path segment)
        url: type.url,
        imageUrl: type.image_url,
        allowedUserTypes: type.allowed_user_types || [],
        unreadCount: Number(type.unread?.count) || 0,
        hasOnlineOperators: type.has_online_operators ?? null,
        welcomeMessage: type.welcome_message ?? null,
        pinnedMessage: type.pinned_message ?? null,
        // Support availability (opening hours + out-of-hours message). Defaults
        // to available when the backend doesn't provide a schedule.
        isAvailable: type.is_available ?? true,
        unavailableMessage: type.unavailable_message ?? null,
        supportSchedule: type.support_schedule ?? null,
      }));

      transformedSupportTypes.forEach((supportType) => {
        console.log(
          `[ChatUnread][support] id=${supportType.id} name="${supportType.name || ""}" unreadCount=${supportType.unreadCount}`,
        );
      });

      return {
        supportTypes: transformedSupportTypes,
        total: data?.total || transformedSupportTypes.length,
        hasMore: data?.hasMore || false,
      };
    } catch (error) {
      console.error("Error in getSupportTypes:", error);
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Support types the signed-in operator is allowed to handle, for the Area Team
 * console. Distinct from getSupportTypes, which lists every type so a customer
 * can pick one to write to.
 */
export const getAuthorizedSupportTypes = createAsyncThunk(
  "streamChat/getAuthorizedSupportTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatFetch(
        `${baseUrl}/api/v1/chat/support/authorized`,
        {
          method: "GET",
          headers: headersDefault(),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || "Error getting authorized support types",
        );
      }

      const data = await response.json();

      return (data?.data || []).map((type) => ({
        id: type.id,
        name: type.name,
        description: type.description,
        imageUrl: type.image_url,
        // Conversations of this type still waiting for a team reply — the
        // number the operator actually needs to see.
        awaitingReplyCount: Number(type.awaiting_reply_count) || 0,
        enabledGroupsCount: Number(type.enabled_groups_count) || 0,
        unreadCount: Number(type.unread?.count) || 0,
      }));
    } catch (error) {
      console.error("Error in getAuthorizedSupportTypes:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Action to create support chat from backend
export const createSupportChat = createAsyncThunk(
  "streamChat/createSupportChat",
  async (supportData, { rejectWithValue }) => {
    try {
      const response = await chatFetch(
        `${baseUrl}/api/v1/chat/support/${supportData.type}`,
        {
          method: "POST",
          headers: headersDefault(),
          body: JSON.stringify({
            type: supportData.type,
          }),
        },
      );


      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error creating support chat:", errorData);
        throw new Error(
          errorData.error?.message ||
            errorData.message ||
            "Error creating support chat",
        );
      }

      const data = await response.json();

      return data;
    } catch (error) {
      console.error("Error in createSupportChat:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Search Users for Chat Creation
export const getUsersSearch = createAsyncThunk(
  "streamChat/getUsersSearch",
  async ({ keyword = "", limit = 100 } = {}, { rejectWithValue }) => {
    try {
      console.log(
        "Debug - Searching users with keyword:",
        keyword,
        "limit:",
        limit,
      );

      // Build query parameters - keyword is always required by backend
      const params = new URLSearchParams();
      params.append("keyword", keyword.trim()); // Always send keyword, even if empty

      if (limit) {
        params.append("limit", limit.toString());
      }

      const url = `${baseUrl}/api/v1/chat/users/search?${params.toString()}`;
      console.log("Debug - Users search URL:", url);

      const response = await chatFetch(url, {
        method: "GET",
        headers: headersDefault(),
      });

      console.log("Debug - Users search response status:", response.status);

      if (!response.ok) {
        if (response.status === 404) {
          console.log("Debug - No users found (404)");
          return {
            users: [],
            hasMore: false,
            total: 0,
          };
        }
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error searching users:", errorData);
        throw new Error(errorData.message || "Error searching users");
      }

      const data = await response.json();

      console.log("Debug - Users search successful:", data);

      // Transform backend data to a more usable format
      const users = data.data.map((user) => ({
        id: user.id,
        providerUserId: user.provider_user_id,
        displayName: user.display_name,
        systemUserName: user.system_user_name,
        systemUserSurname: user.system_user_surname,
        userType: user.user_type,
      }));

      return {
        users,
        hasMore: data.hasMore || false,
        total: users.length,
        nextCursor: data.nextCursor,
        previousCursor: data.previousCursor,
      };
    } catch (error) {
      console.error("Error in getUsersSearch:", error);
      return rejectWithValue(error.message);
    }
  },
);

// Create 1:1 Channel with User
export const createChannel = createAsyncThunk(
  "streamChat/createChannel",
  async ({ memberUserId, memberUserType = "system" }, { rejectWithValue }) => {
    try {
      console.log(
        "Debug - Creating channel with user ID:",
        memberUserId,
        "type:",
        memberUserType,
      );

      const requestBody = {
        member_user_id: memberUserId,
        member_user_type: memberUserType,
      };

      const response = await chatFetch(`${baseUrl}/api/v1/chat/channels`, {
        method: "POST",
        headers: headersDefault(),
        body: JSON.stringify(requestBody),
      });

      console.log("Debug - Create channel response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Debug - Error creating channel:", errorData);
        throw new Error(errorData.message || "Error creating channel");
      }

      const data = await response.json();

      console.log("Debug - Channel created successfully:", data);

      return data;
    } catch (error) {
      console.error("Error in createChannel:", error);
      return rejectWithValue(error.message);
    }
  },
);
